// Formatting and date helpers shared by every report screen.
// Presentation only - nothing here touches API payloads.
import dayjs from "dayjs";

export const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const num = (v) => Number(v) || 0;

export const money = (v) =>
  num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const intg = (v) => num(v).toLocaleString("en-US", { maximumFractionDigits: 3 });

export const pct2 = (v) => (Math.round(num(v) * 100) / 100).toFixed(2);

// 1 234 567 -> "1.2M", for the chart's y axis
export const short = (v) => {
  const a = Math.abs(v);
  if (a >= 1000000) return (v / 1000000).toFixed(a >= 10000000 ? 0 : 1) + "M";
  if (a >= 1000) return (v / 1000).toFixed(a >= 10000 ? 0 : 1) + "k";
  return String(Math.round(v));
};

export const iso = (d) => d.format("YYYY-MM-DD");

// "2026-09-12" -> "12 Sep 26"
export const dmy = (s) => {
  const d = dayjs(s);
  return d.isValid() ? `${d.format("DD")} ${MON[d.month()]} ${d.format("YY")}` : "—";
};

export const dm = (s) => {
  const d = dayjs(s);
  return d.isValid() ? `${d.format("DD")} ${MON[d.month()]}` : "—";
};

export const monthLabel = (k) => {
  const [y, m] = String(k).split("-");
  return MON[+m - 1] ? `${MON[+m - 1]} ${y}` : String(k);
};

export const daysBetween = (a, b) => dayjs(b).diff(dayjs(a), "day");

export const weekKey = (s) => {
  const d = dayjs(s);
  return iso(d.subtract((d.day() + 6) % 7, "day"));
};

// a record's date as YYYY-MM-DD, whatever the API sent
export const dateOf = (v) => {
  if (!v) return "";
  const d = dayjs(v);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

/* ================= period presets ================= */
export const PRESETS = [
  { id: "today", label: "Today" },
  { id: "yday", label: "Yesterday" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "tmonth", label: "This month" },
  { id: "lmonth", label: "Last month" },
  { id: "quarter", label: "This quarter" },
  { id: "ytd", label: "Year to date" },
  { id: "all", label: "All time" },
  { id: "custom", label: "Custom range" },
];

// [from, to] as YYYY-MM-DD; ["",""] means no date filter at all
export const presetRange = (id) => {
  const t = dayjs();
  switch (id) {
    case "today":
      return [iso(t), iso(t)];
    case "yday":
      return [iso(t.subtract(1, "day")), iso(t.subtract(1, "day"))];
    case "7d":
      return [iso(t.subtract(6, "day")), iso(t)];
    case "30d":
      return [iso(t.subtract(29, "day")), iso(t)];
    case "tmonth":
      return [iso(t.startOf("month")), iso(t)];
    case "lmonth": {
      const m = t.subtract(1, "month");
      return [iso(m.startOf("month")), iso(m.endOf("month"))];
    }
    case "quarter": {
      const q = Math.floor(t.month() / 3) * 3;
      return [iso(t.month(q).startOf("month")), iso(t)];
    }
    case "ytd":
      return [iso(t.startOf("year")), iso(t)];
    case "all":
      return ["", ""];
    default:
      return null;
  }
};

export const CHART_LABEL = {
  "30d": "Last 30 days",
  "7d": "Last 7 days",
  "90d": "Last 90 days",
  ytd: "Year to date",
  match: "Report period",
};

/* ================= aggregation ================= */
// Sum `fields` over `rows` grouped by keyFn. `seed` adds the non-numeric
// columns (name, unit, ...) the first time a group is seen.
export const groupSum = (rows, keyFn, fields, seed) => {
  const m = {};
  rows.forEach((r) => {
    const k = keyFn(r);
    if (k === undefined || k === null || k === "") return;
    if (!m[k]) {
      m[k] = { key: k, n: 0, ...(seed ? seed(r) : {}) };
      fields.forEach((f) => (m[k][f] = 0));
    }
    m[k].n += 1;
    fields.forEach((f) => (m[k][f] += num(r[f])));
  });
  return Object.values(m);
};

export const sumBy = (rows, field) => rows.reduce((a, r) => a + num(r[field]), 0);

// two-letter tile text: "Al Rawabi Supermarket" -> "AR"
export const initialsOf = (name) => {
  const words = String(name || "")
    .replace(/[^\p{L}\p{N} ]/gu, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "—";
  const first = words[0];
  const second = words[1] ? words[1][0] : first[1] || "";
  return (first[0] + second).toUpperCase();
};

// stable hue per key, so a customer always gets the same tile colour
export const hueOf = (key) => {
  const s = String(key || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return s ? h % 360 : 228;
};
